import {
    Alert,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import React, {useContext, useState} from 'react'
import {AppContext} from '../../contexts/AppContextProvider'
import {AvailableToReturnState} from '../../contexts/types'
import {useForm} from '../../hooks/useForm'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {setAvailableToReturn} from "../../contexts/AvailableToReturnReducer";
import CNBSRadioGroupInput from "../Inputs/CNBSRadioGroupInput";
import useDefaultRadioOptions from "../../hooks/useDefaultRadioOptions";
import ProductSearchInput from "../Inputs/ProductSearchInput";
import SelectedProductDisplay from "../SelectedProductDisplay";
import {setMainProductReplacement, setSecondaryDeviceReplacement} from "../../contexts/ReplacementsReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";
import {db} from "../../lib/db";
import {useLiveQuery} from "dexie-react-hooks";

const AvailableToReturn: React.FC = () => {
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {complaint, dispatch, postNotification, clearNotification} = useContext(AppContext)
    const {t, questionsConfiguration} = useContext(OrgCustomizationContext)
    const {yesNoRadioOptions} = useDefaultRadioOptions()
    const required = {value: true, message: t('this_field_is_required')}
    const [fileSelectionError, setFileSelectionError] = useState<string>('')
    const {
        data,
        errors,
        handleChangeEvent,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<AvailableToReturnState>({
        initialValues: {
            ...complaint.availableToReturn,
        },
        validations: {
            canItemBeReturned: {required},
            confirmDecontamination: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.canItemBeReturned === 'YES' &&
                            !data.confirmDecontamination
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            reasonItemCannotBeReturned: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.canItemBeReturned === 'NO' &&
                            !data.reasonItemCannotBeReturned.trim()
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            attachmentId: {
                required: {
                    value: (value, data) => {
                        return !(
                            (questionsConfiguration.browse_for_photos?.required && data.canItemBeReturned === 'NO') &&
                            !data.attachmentId
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
        },
    })
    const currentAttachment = useLiveQuery(() => data.attachmentId ? db.attachments.get(data.attachmentId) : undefined, [data.attachmentId])

    const navigateToNextScreen = () => navigateForward()

    const handleSubmit = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid ||
            (complaint.generalComplaint.isReplacementNeeded === 'YES' && !complaint.replacements.selectedMainProductReplacement) ||
            (complaint.secondaryDevice.isReplacementNeeded === 'YES' && !complaint.replacements.selectedSecondaryDeviceReplacement)
        ) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Clear attachment if the user has selected yes
        if (complaint.availableToReturn.canItemBeReturned === 'NO') {
            if (data.canItemBeReturned === 'YES') {
                data.attachmentId = undefined
            }
        }

        // Package up the form data and update app state
        const availableToReturn: AvailableToReturnState = {
            ...data,
            confirmDecontamination:
                data.canItemBeReturned === 'YES'
                    ? data.confirmDecontamination
                    : false,
            reasonItemCannotBeReturned:
                data.canItemBeReturned === 'NO'
                    ? data.reasonItemCannotBeReturned
                    : '',
        }

        dispatch(setAvailableToReturn(availableToReturn))

        navigateToNextScreen()
    }

    const hiddenFileInput = React.useRef<HTMLInputElement>(null)

    const handleUploadClick = (e: React.MouseEvent) => {
        if (hiddenFileInput) {
            hiddenFileInput!.current!.click()
        }
    }

    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileSelectionError('')

        if (e.target.files) {
            if (e.target.files[0]?.size > (5 * 1048576)) { // binary 5MB
                setFileSelectionError(t('multi_image_file_too_large', {
                    filename: e.target.files[0].name,
                    maxSizeInMb: '5'
                }))
                return
            }

            if (currentAttachment) {
                await db.attachments.delete(currentAttachment.id)
            }

            data.attachmentId = await db.attachments.add({
                name: e.target.files[0].name,
                content: e.target.files[0],
                tag: 'xray'
            })

            validateFields(data)
        }
    }

    return (
        <Grid container>
            <Stack spacing={2} sx={{width: '100%'}}>
                {/*<Stack spacing={2}>*/}
                <Typography variant='h5' sx={{mb: 1}}>
                    {t('atr_available_to_return')}
                </Typography>

                <CNBSRadioGroupInput label={t('atr_can_item_be_returned')}
                                     value={data.canItemBeReturned}
                                     onChange={handleChangeEvent('canItemBeReturned')}
                                     options={yesNoRadioOptions}
                                     error={errors.canItemBeReturned}/>

                {data.canItemBeReturned === 'YES' && (
                    <FormControl component='fieldset'>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox color='primary'
                                                   checked={data.confirmDecontamination}
                                                   onChange={e => handleChange('confirmDecontamination', e.target.checked)}/>}
                                label={t('atr_confirm_decontamination')}/>
                        </FormGroup>
                        <FormHelperText error={!!errors.confirmDecontamination}>
                            {errors.confirmDecontamination}
                        </FormHelperText>
                    </FormControl>
                )}

                {data.canItemBeReturned === 'NO' && (
                    <>
                        <FormControl sx={{pt: 0}}>
                            <FormLabel sx={{mb: 1, maxWidth: '750px'}}>
                                {t('atr_please_give_a_reason')}:
                            </FormLabel>
                            <TextField
                                multiline
                                minRows={2}
                                maxRows={4}
                                error={!!errors.reasonItemCannotBeReturned}
                                helperText={errors.reasonItemCannotBeReturned}
                                value={data.reasonItemCannotBeReturned}
                                onChange={handleChangeEvent(
                                    'reasonItemCannotBeReturned'
                                )}
                            />
                        </FormControl>
                        <Typography
                            variant='body2'
                            sx={{mt: 1, maxWidth: '750px'}}
                        >
                            {t('atr_if_implant_not_available')}
                        </Typography>

                        {/*Attachment*/}
                        {
                            questionsConfiguration.browse_for_photos?.show &&
                            <>
                                <Stack
                                    direction={{xs: 'column', sm: 'row'}}
                                    alignItems={{xs: 'flex-start', sm: 'flex-end'}}
                                    spacing={2}
                                >
                                    <FormControl>
                                        <FormLabel sx={{mb: 1}}>
                                            {t('atr_browse_for_photos')}:
                                        </FormLabel>
                                        <TextField
                                            size='small'
                                            sx={{maxWidth: '20rem'}}
                                            value={currentAttachment?.name || ''}
                                        />
                                    </FormControl>

                                    <Button
                                        onClick={handleUploadClick}
                                        variant='contained'
                                        component='label'
                                        sx={{height: '40px', pt: -1}}
                                    >
                                        {t('upload')}
                                    </Button>
                                    <input
                                        type='file'
                                        ref={hiddenFileInput}
                                        onChange={handleFileInputChange}
                                        id='xray'
                                        name='xray'
                                        accept='image/png, image/jpg, image/jpeg, application/pdf'
                                        multiple={false}
                                        hidden
                                    />
                                </Stack>
                                <>
                                    {!!errors.attachmentId && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: '#d32f2f',
                                                pl: '1rem',
                                                pt: '0',
                                            }}
                                        >
                                            {errors.attachmentId}
                                        </Typography>
                                    )}
                                </>
                            </>
                        }

                        {
                            questionsConfiguration.browse_for_photos?.show &&
                            fileSelectionError &&
                            <Alert color='error'>{fileSelectionError}</Alert>
                        }
                    </>
                )}

                {complaint.generalComplaint.isReplacementNeeded === 'YES' && (
                    <>
                        <Stack spacing={2} sx={{pb: 2}}>
                            <Typography variant='h5' sx={{mb: 1}}>
                                {t('mpr_main_product_replacement')}
                            </Typography>
                        </Stack>

                        {!complaint.replacements.selectedMainProductReplacement && <ProductSearchInput
                            searchCurrentProducts
                            value={complaint.replacements.selectedMainProductReplacement}
                            onChange={(product) => dispatch(setMainProductReplacement(product))}
                        />}

                        {complaint.replacements.selectedMainProductReplacement && <SelectedProductDisplay
                            product={complaint.replacements.selectedMainProductReplacement}
                            sx={{mt: 3}}
                            endAdornment={<Button sx={{minHeight: 0, minWidth: 0, padding: 0, textAlign: 'left'}}
                                                  onClick={() => dispatch(setMainProductReplacement(undefined))}>
                                {t('click_here_to_change_article_number')}
                            </Button>}
                        />}
                    </>
                )}

                {complaint.secondaryDevice.isReplacementNeeded === 'YES' && (
                    <>
                        <Stack spacing={2} sx={{pb: 2}}>
                            <Typography variant='h5' sx={{mb: 1}}>
                                {t('spr_secondary_product_replacement')}
                            </Typography>
                        </Stack>

                        {!complaint.replacements.selectedSecondaryDeviceReplacement && <ProductSearchInput
                            searchCurrentProducts
                            value={complaint.replacements.selectedSecondaryDeviceReplacement}
                            onChange={product => dispatch(setSecondaryDeviceReplacement(product))}
                        />}

                        {complaint.replacements.selectedSecondaryDeviceReplacement && <SelectedProductDisplay
                            product={complaint.replacements.selectedSecondaryDeviceReplacement}
                            sx={{mt: 3}}
                            endAdornment={<Button sx={{minHeight: 0, minWidth: 0, padding: 0, textAlign: 'left'}}
                                                  onClick={() => dispatch(setSecondaryDeviceReplacement(undefined))}>
                                {t('click_here_to_change_article_number')}
                            </Button>}
                        />}
                    </>
                )}
            </Stack>

            <Stack
                direction='row-reverse'
                spacing={2}
                sx={{pt: 4, width: '100%'}}
            >
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setAvailableToReturn(data))}/>

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default AvailableToReturn
